import { Box, Button } from "@material-ui/core";
import { graphql } from "gatsby";
import React, { useState } from "react";
import fastCompare from "react-fast-compare";
import { useParams } from "@reach/router";
import ShopingCartIcon from "../../components/icons/shopping-cart";
import LocalizedLink from "../../components/localized-link";
import StoryModal from "../../components/story-modal";
import { getLinkHref } from "../card/utils";
import clsx from "clsx";
import OpenInNew from "@material-ui/icons/OpenInNew";
import ProductDetailsModal from "../../containers/shop/ProductDetailsModal";

type Props = Queries.ButtonSectionFragmentFragment;

const getIconByDatoCmsName = (datoCmsName: string) => {
  const ICON_MAP = {
    cart: ShopingCartIcon,
    openInNew: OpenInNew
  };
  if (ICON_MAP[datoCmsName]) {
    return React.createElement(ICON_MAP[datoCmsName]);
  }
  return null;
};

function ButtonSection({
  text,
  link,
  size,
  color,
  width,
  variant,
  icon,
  iconPosition,
  maxWidth,
  analyticsEvent
}: Props) {
  const routeParams = useParams();
  const handleCtaStoryClick = () => {
    setStoryModalOpen(true);
  };

  const isLinkToStory = link?.model?.apiKey === "story";
  const [isStoryModalOpen, setStoryModalOpen] = useState(false);

  const isLinkToProductDetails = link?.model?.apiKey === "product";
  const [isProductDetailsModalOpen, setProductDetailsModalOpen] =
    useState(false);

  const buttonProps = {
    variant: variant as any,
    color:
      color === "white" || color === "express-delivery" || !color
        ? undefined
        : (color as any),
    fullWidth: width === "full-width",
    size:
      size && size.length > 0 && size !== "default" ? (size as any) : undefined,
    className: clsx({
      "express-delivery": color === "express-delivery",
      "MuiButton-sizeXLarge": size === "xlarge"
    }),
    startIcon:
      icon && iconPosition === "start" ? getIconByDatoCmsName(icon) : undefined,
    endIcon:
      icon && iconPosition === "end" ? getIconByDatoCmsName(icon) : undefined,
    style: maxWidth ? { maxWidth } : undefined
  };

  return (
    <Box display="flex" justifyContent="center">
      {/* Internal/External link button */}
      {!isLinkToStory && !isLinkToProductDetails && (
        // @ts-ignore
        <Button
          {...buttonProps}
          component={LocalizedLink}
          to={getLinkHref(link, { routeParams })}
          data-analytics={analyticsEvent}
        >
          {text}
        </Button>
      )}

      {/* Story button */}
      {isLinkToStory && (
        <Button
          {...buttonProps}
          onClick={handleCtaStoryClick}
          data-analytics={analyticsEvent}
        >
          {text}
        </Button>
      )}

      {/* Story modal */}
      {isLinkToStory && (
        <StoryModal
          open={isStoryModalOpen}
          onClose={() => setStoryModalOpen(false)}
          story={link as Queries.DatoCmsStory}
        />
      )}

      {/* Product details modal */}
      {isLinkToProductDetails && (
        <>
          {/* @ts-ignore */}
          <Button
            {...buttonProps}
            data-analytics={analyticsEvent}
            onClick={() => setProductDetailsModalOpen(true)}
          >
            {text}
          </Button>
          <ProductDetailsModal
            open={isProductDetailsModalOpen}
            onClose={() => setProductDetailsModalOpen(false)}
            productSelector={{
              slug: (link as Queries.DatoCmsProduct).slug
            }}
          />
        </>
      )}
    </Box>
  );
}
export default React.memo(ButtonSection, fastCompare);

export const ButtonSectionFragment = graphql`
  fragment ButtonSectionFragment on DatoCmsCModuleButtonSection {
    text
    variant
    width
    color
    size
    icon
    iconPosition
    maxWidth
    analyticsEvent
    link {
      ... on DatoCmsWebPage {
        path
        model {
          apiKey
        }
      }
      ... on DatoCmsStory {
        ...StoryFragment
      }
      ... on DatoCmsCardExternalLink {
        url
        model {
          apiKey
        }
      }
      ... on DatoCmsProduct {
        slug
        productType
        model {
          apiKey
        }
      }
    }
    container
    innerContainer
    containerDisableGutters
    containerBackgroundColor
    visibilitySettings {
      ...ContentModuleVisibilitySettingFragment
    }
    model {
      apiKey
    }
  }
`;
